import { INNER_CELL, INNER_TABLE, TABLE, TABLE_CELL, TABLE_HEAD, TableTitle } from './TableCard';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useDragScroll } from './useDragScroll';
import { cssRgb } from './sampleColour';
import { useLanguage } from '../../contexts/LanguageContext';
import { useStableText } from '../../components/StableText';
import { DefaultMaterialRow, DefaultMaterialsButtons } from './DefaultMaterials';
import { MaterialPartsPicker, useMaterialParts, type PickablePart } from './MaterialPartsPicker';
import { listingMaterials, type PartNames } from './partMaterialRun';
import { surfaceKey } from './materialSurface';
import { useSubmodels } from './listingStore';
import { useSubmodelListingCrops } from './listingCropsRun';
import { useStandardMaterials } from './standardMaterials';
import { materialText, type MaterialPair } from './variantLabel';
import {
  byName, mapSrc, setReadingColour, setReadingPick, type Material, type Pattern, type MaterialsAnswer, type TextureStat, type Textured,
  namesOf,
} from './materialsRun';

// THE MATERIALS, ONE TABLE: every material this colourway's parts can be made of, what each is
// judged to be, which parts wear it, and what its patches measure.
//
// THREE KINDS OF ROW, in this order. The listing's own materials — what the product is made of,
// whether or not a reading has judged them yet. Then any the reading named that the listing does
// not (the model's own, for a part nobody gave one). Then the default materials, the hardware's,
// edited by hand (see DefaultMaterials). They were three tables — this one, what each part is
// made of, and the defaults — about the same list of materials.
//
// Read left to right: what the model said, what the pixels say, the parts wearing it, and the
// texture map the two produce with the numbers it is drawn from. The patches the pixels were
// read off are the patches call's answer, shown with that call (see MaterialPatches).
export function MaterialsTable({ docId, submodel, answer, applied, locked, pending, applyProduct, applyStandard, only, title }: {
  docId: string;
  submodel: string;
  // The reading on screen — possibly none yet, and the rows are there anyway.
  answer: MaterialsAnswer;
  // Whether it is the one the meshes wear. One that is not shows the parts IT gave each material,
  // editable, into that reading: they become the parts' when it is applied.
  applied: boolean;
  locked?: boolean;
  // A COMPONENTS READING IS OUT: every cell it will fill spins until it lands — the colours, the
  // parts, the two numbers, the texture.
  pending?: boolean;
  // THE TWO APPLIES, drawn by the panel that holds the reading: under the product's rows, and
  // beside the standard materials' add.
  applyProduct?: ReactNode;
  applyStandard?: ReactNode;
  // HALF OF IT, under a title of its own: a colourway's product materials, or the standard ones,
  // which are every colourway's (see MaterialsPanel, which draws one table per colourway).
  only?: 'product' | 'standard';
  title?: string;
}) {
  const { t } = useLanguage();
  const st = useStableText();
  const { materials, crops, stats } = answer;
  const product = only !== 'standard';
  const standard = only !== 'product';

  const submodels = useSubmodels(docId);
  const defaults = useStandardMaterials(docId);
  const partsNow = useMaterialParts(docId, submodel);
  const proposed = !applied && answer.partPicks;
  // AND ITS HARDWARE HALF, the same way: the parts it put on each default material, and what it
  // said of each, until THAT HALF is applied — by its own button, whatever the product's apply
  // did (see HardwareReading.applied).
  const hwApplied = !!answer.hardware?.applied;
  const hardware = !hwApplied ? answer.hardware : undefined;
  const worn = { ...partsNow.worn };
  for (const at of proposed ? proposed.asked : []) {
    if (proposed && proposed.picks[at]) worn[at] = [proposed.picks[at]];
    else delete worn[at];
  }
  const partOf = { ...partsNow.partOf };
  for (const at of hardware?.asked ?? []) {
    const got = (hardware?.picks ?? []).filter((one) => one.at === at);
    if (got.length) worn[at] = got.map((one) => one.pair);
    else delete worn[at];
    partOf[at] = namesOf(got);
  }
  // EVERY READING'S PARTS ARE EDITABLE. A tick on one that is not applied changes that reading
  // (written onto the parts when it is); on the applied one it changes the parts now, as it always
  // did, and the reading with them — so applying it again does not undo the hand's work.
  const pick = (part: PickablePart, pairs: MaterialPair[], of?: PartNames) => {
    if (answer.id) setReadingPick(docId, submodel, answer.id, part.at, pairs, part.hardware, of);
  };
  // A tool's part, named: the same pairs, one word changed.
  const named = (part: PickablePart, pair: MaterialPair, text: string, on: typeof worn, of: typeof partOf) =>
    pick(part, on[part.at] ?? [], { ...(of[part.at] ?? {}), [surfaceKey(pair)]: text.trim() });
  // A PART IS LIVE when its half of the reading is applied — the components with the reading,
  // the hardware with its own half — or when there is no reading: a tick changes it now.
  // `worn` and `partOf` hold what is live as it is, so they serve both.
  const live = (part: PickablePart) => !answer.id || (part.hardware ? hwApplied : applied);
  const partsOf = {
    ...partsNow, worn, partOf,
    put: (part: PickablePart, pairs: MaterialPair[], of?: PartNames) => {
      if (live(part)) partsNow.put(part, pairs, of);
      pick(part, pairs, of);
    },
    name: (part: PickablePart, pair: MaterialPair, text: string) => {
      if (live(part)) partsNow.name(part, pair, text);
      named(part, pair, text, worn, partOf);
    },
  };
  const edit = (material: string) => (locked || !answer.id
    ? undefined
    : (rgb: [number, number, number]) => setReadingColour(docId, submodel, answer.id, material, rgb));

  const rows = useMemo(() => {
    // A READING'S MATERIAL IS THE LISTING'S when it bears its words — the call is given them and
    // told to use them (see writtenParts), so this matches on the text, not the case.
    const norm = (s: string) => s.trim().toLowerCase();
    const listed = listingMaterials(submodels, submodel);
    const judgedAs = (name: string) => materials.find((m) => norm(m.name) === norm(name));
    const row = (name: string, pair: MaterialPair | null, judged: Material | undefined) => {
      const key = judged?.name ?? name;
      return {
        name, pair, judged,
        read: stats.find((one) => one.material === key),
        wrote: answer.wrote?.[key],
      };
    };
    return byName([
      ...listed.map((pair) => row(materialText(pair), pair, judgedAs(materialText(pair)))),
      ...materials
        .filter((m) => !listed.some((pair) => norm(materialText(pair)) === norm(m.name)))
        .map((m) => row(m.name, null, m)),
    ]);
  }, [submodels, submodel, materials, stats, answer.wrote]);

  const found = crops.length;
  // WHETHER PHASE 2 HAS CLOSE-UPS NOW, in THIS TABLE'S colourway (not the one chosen in the
  // sidebar) — the reading on screen may predate them, and then the thing to do is read again,
  // not go back to Phase 2.
  const { byPart } = useSubmodelListingCrops(docId, submodel);
  const cropsNow = Object.values(byPart).some((list) => list.some((c) => !c.off));
  // Dragged sideways like the parts table: seventeen columns do not fit a page.
  const scrollRef = useDragScroll<HTMLDivElement>();
  // Every column: the five named ones and the defaults' bin.
  const span = 5 + 1;
  const section = (label: ReactNode) => (
    <tr>
      <td colSpan={span} className={`${cell} bg-slate-50 py-1.5 text-center text-[11px] font-bold uppercase tracking-wider text-slate-500`}>
        {label}
      </td>
    </tr>
  );
  const cell = TABLE_CELL;
  const head = TABLE_HEAD;
  // ON A PHONE, A MATERIAL IS A CARD, NOT A ROW. Six columns are 800px of table, and a phone
  // scrolled it sideways under a clipped header. Stacked instead: the name beside its colour,
  // then the parts, the numbers, the map and the bin, each across the card (the numbers and the
  // map are 300px side by side, wider than the phone). The column heads go, since each cell says
  // what it is (the swatch, the picker, the labelled numbers, the captioned map). By position, so
  // the default materials' rows, which are this table's too, stack the same way.
  const phone = [
    'max-sm:[&>thead]:hidden',
    'max-sm:[&>tbody>tr]:mb-2 max-sm:[&>tbody>tr]:grid max-sm:[&>tbody>tr]:grid-cols-2',
    'max-sm:[&>tbody>tr>td]:flex max-sm:[&>tbody>tr>td]:flex-col max-sm:[&>tbody>tr>td]:items-center max-sm:[&>tbody>tr>td]:justify-center',
    'max-sm:[&>tbody>tr>td:nth-child(n+3)]:col-span-2',
    'max-sm:[&>tbody>tr>td[colspan]]:col-span-2 max-sm:[&>tbody>tr>td:empty]:hidden',
  ].join(' ');

  return (
    <section className="w-full">
      <TableTitle>{title ?? t('notice.3d.materialsTitle')}</TableTitle>

      {/* DRAWN WITH NO PATCHES TOO: the judged half of every row is there from the first call,
          and the measured half says with dashes which materials the second has not answered. */}
      <div ref={scrollRef} className="w-full cursor-grab overflow-x-auto">
          <table className={`${TABLE} ${phone}`}>
            <thead>
              <tr>
                <th className={head}>{st('notice.3d.material')}</th>
                <th className={head}>{st('notice.3d.colour')}</th>
                {/* ONE COLOUR, the model's — the one painted. The pixels' own came out within a few
                    units of it reading after reading (the model's colour picks which pixels are
                    the material), so it was a second column saying the same thing. */}
                <th className={head}>{st('notice.3d.materialParts')}</th>
                {/* THE NUMBERS, THEN THE PICTURE THEY DRAW — roughness and metallic, then contrast,
                    chroma, directional and period (see TextureValues), and the map last. */}
                <th className={head}>{st('notice.3d.texValues')}</th>
                <th className={head}>{st('notice.3d.texMap')}</th>
                {/* THE PATCHES THEMSELVES ARE NOT HERE: they are the patches call's answer, and
                    are shown with it (see MaterialPatches, in the badge's panel). */}
                <th className={head} />
              </tr>
            </thead>
            <tbody>
              {product && !only && rows.length > 0 && section(st('notice.3d.listingMaterials'))}
              {/* NO TEXTURE WITHOUT PART CROPS: the measurement reads the parts' own pixels, which
                  Phase 2's crops row finds — a row with its own button, left out of a New run.
                  Said at the head of the rows whose texture it is missing, not under the table —
                  and not while a reading is out, which is the answer to it. */}
              {product && rows.length > 0 && found === 0 && materials.length > 0 && !pending && (
                <tr>
                  <td colSpan={span} className={`${cell} py-2 text-center text-xs text-amber-700`}>
                    {t(cropsNow ? 'notice.3d.textureStale' : 'notice.3d.textureNone')}
                  </td>
                </tr>
              )}
              {product && rows.map((row) => (
                <tr key={row.name}>
                  <td className={`${cell} font-semibold`}>
                    {row.name}
                    {/* A NAME THE LISTING DOES NOT HAVE: the reading answered with a material it
                        was not given, which is no part's to be picked into. */}
                    {!row.pair && (
                      <span className="block text-[10.5px] font-normal italic text-amber-600">
                        {st('notice.3d.materialNotGiven')}
                      </span>
                    )}
                  </td>
                  {/* EDITABLE, as the hardware's swatch is: a correction kept on the reading. */}
                  <td className={cell}>
                    {pending ? spin : row.judged ? (
                      <ColourCell rgb={row.judged.rgb} onChange={edit(row.judged.name)} />
                    ) : dash}
                  </td>
                  <td className={cell}>
                    {/* A MATERIAL THE LISTING DOES NOT NAME is no part's to pick: it is the model's
                        word, and a part is made of the listing's (see materialOptions). */}
                    {pending ? spin : row.pair
                      ? <MaterialPartsPicker pair={row.pair} listed disabled={locked} {...partsOf} />
                      : dash}
                  </td>
                  <td className={cell}>
                    {pending ? spin : <TextureValues surface={row.judged} read={row.read} wrote={row.wrote} />}
                  </td>
                  <td className={pending ? cell : `${cell} align-top`}>
                    {pending ? spin : <TextureMap name={row.name} pattern={row.judged?.pattern} read={row.read} wrote={row.wrote} />}
                  </td>
                  {/* The defaults' bin column: nothing to remove on a photographed material. */}
                  <td className={cell} />
                </tr>
              ))}
              {product && applyProduct && (
                <tr>
                  <td colSpan={span} className={`${cell} py-3`}>
                    <div className="flex justify-center">{applyProduct}</div>
                  </td>
                </tr>
              )}
              {standard && !only && defaults.length > 0 && section(st('notice.3d.defaultMaterials'))}
              {standard && defaults.map((m, at) => (
                <DefaultMaterialRow
                  key={at}
                  docId={docId}
                  at={at}
                  cell={cell}
                  parts={<MaterialPartsPicker pair={m.pair} listed={false} disabled={locked} {...partsOf} />}
                  proposed={hardware?.materials.find((one) => one.pair.material === m.pair.material
                    && one.pair.color === m.pair.color)}
                />
              ))}
            </tbody>
          </table>
      </div>

      {standard && (
        <div className="mt-3 flex flex-wrap justify-center gap-2">
          <DefaultMaterialsButtons docId={docId} />
          {applyStandard}
        </div>
      )}
    </section>
  );
}

// A COLOUR AS A SWATCH, ITS HEX UNDER IT — the judged one, the measured one and a default's
// alike, so the three read the same down the column. A default's is its own to change: the
// swatch is then the colour picker, which is why the words are under it and not beside it.
export function ColourCell({ rgb, onChange }: {
  rgb: [number, number, number];
  onChange?: (rgb: [number, number, number]) => void;
}) {
  const hex = `#${rgb.map((c) => Math.max(0, Math.min(255, c)).toString(16).padStart(2, '0')).join('')}`;
  const swatch = 'block h-4 w-4 rounded border border-slate-400';
  return (
    <span className="inline-flex flex-col items-center gap-0.5">
      {onChange ? (
        <label className="relative cursor-pointer" title={hex}>
          <span className={swatch} style={{ background: cssRgb(rgb) }} />
          <input
            type="color"
            value={hex}
            onChange={(e) => onChange([1, 3, 5].map((k) => parseInt(e.target.value.slice(k, k + 2), 16)) as [number, number, number])}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </label>
      ) : <span className={swatch} style={{ background: cssRgb(rgb) }} />}
      <span className="font-mono text-[10.5px] tabular-nums">{hex}</span>
    </span>
  );
}

const dash = <span className="text-slate-300">—</span>;

// A CELL WHOSE ANSWER IS ON ITS WAY — the materials table's, and the hardware rows' (see
// DefaultMaterialRow).
export const spin = <Loader2 className="mx-auto h-4 w-4 animate-spin text-slate-400" />;


// THE MAP — the tiling picture the material's coarseness draws, and what writing it onto the
// meshes actually did.
//
// DRAWN BY THE SERVER FROM THREE NUMBERS and addressed by them, so this is the very bytes the
// meshes were given rather than a second rendering of the same idea: the seed is a CRC of the
// material's name, so the same measurement always draws the same tile (see materials_map).
//
// WHAT IT SAYS UNDER THE PICTURE is the half a reader cannot see in it: how big one tile is on
// the real object, and — once the reading has been applied — how many triangles had to have
// their corners split because they straddled two projections. That number is the cost of box
// projection, and it is worth having in front of somebody rather than in a log.
type TextureRead = Pick<TextureStat, 'contrast' | 'periodMm' | 'anisotropy'> & { periodPx?: number; chroma?: number };

// THE SURFACE'S NUMBERS, as a small bordered table: the two the paint takes (roughness and
// metallic, judged by the call) and the ones the map is drawn from (measured). They were
// columns of the materials table, one each.
export function TextureValues({ surface, read, wrote }: {
  surface?: { roughness?: number; metallic?: number };
  read?: TextureRead;
  wrote?: Textured;
}) {
  const st = useStableText();
  const num = (v: number | undefined, places: number) => (v == null ? '—' : v.toFixed(places));
  if (surface?.roughness == null && surface?.metallic == null && (!read || (read.contrast == null
    && read.anisotropy == null && read.periodMm == null && read.periodPx == null
    && read.chroma == null))) {
    return <span className="text-slate-300">—</span>;
  }
  return (
      <table className={`${INNER_TABLE} text-[10px] leading-tight text-slate-900`}>
        <tbody>
          {([
            ['notice.3d.roughness', num(surface?.roughness, 2)],
            ['notice.3d.metallic', num(surface?.metallic, 2)],
            ['notice.3d.texContrast', num(read?.contrast, 3)],
            // THE HUE'S SPREAD, drawn as a warm-cool shift in the map (see texture_map.HUE_AXIS).
            ['notice.3d.texChroma', num(read?.chroma, 3)],
            ['notice.3d.texAniso', num(read?.anisotropy, 2)],
            // IN MILLIMETRES WHERE THE OBJECT COULD BE BOXED, and in pixels where it could not —
            // which is what the number honestly is until something in the picture has a size.
            ['notice.3d.texPeriod', read?.periodMm != null ? `${read.periodMm} mm`
              : read?.periodPx != null ? `${num(read.periodPx, 0)} px` : '—'],
            // HOW BIG ONE REPEAT OF THE PICTURE IS on the real object: a whole number of periods
            // across — six rings for a board, ten blotches for a mottle (see texture_map) — and
            // what that comes to. What the last apply drew it at; nothing before one.
            ['notice.3d.texTileSize', wrote?.tileMm != null
              ? `${wrote.repeats != null ? `×${wrote.repeats} · ` : ''}${wrote.tileMm} mm` : '—'],
          ] as const).map(([key, value]) => (
            <tr key={key}>
              <td className={INNER_CELL}>
                <span className="block first-letter:uppercase">{st(key)}</span>
              </td>
              <td className={`${INNER_CELL} tabular-nums`}>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
  );
}

// THE MAP ITSELF.
export function TextureMap({ name, pattern, read, wrote }: {
  name: string;
  // Which of the two surfaces the material is, as the call named it — nothing is drawn without.
  pattern?: Pattern;
  read?: TextureRead;
  wrote?: Textured;
}) {
  const st = useStableText();
  // THE RING SPACING IS WHAT IS MEASURED; the tile is a whole number of them, so drawing the
  // map before an apply means asking for the same spacing the apply would.
  const ring = wrote?.ringMm ?? read?.periodMm ?? 40;
  const aniso = wrote?.anisotropy ?? read?.anisotropy ?? 0;
  const src = read?.contrast && pattern
    ? mapSrc(name, pattern, read.contrast, ring, aniso, read.chroma) : null;
  // THE SERVER DRAWS IT, which takes a moment: a spinner until those bytes have landed, and a
  // dash for a map that could not be drawn — an empty frame said neither.
  const [landed, setLanded] = useState<{ src: string; ok: boolean } | null>(null);
  useEffect(() => {
    if (!src) return;
    const img = new Image();
    img.onload = () => setLanded({ src, ok: true });
    img.onerror = () => setLanded({ src, ok: false });
    img.src = src;
    return () => { img.onload = null; img.onerror = null; };
  }, [src]);
  if (!read?.contrast || !src) {
    return <span className="text-slate-300">—</span>;
  }
  const state = landed?.src === src ? (landed.ok ? 'ok' : 'failed') : 'loading';
  return (
    <figure className="flex w-32 flex-col items-center gap-0.5">
      {/* FOUR TILES, TWO BY TWO, so the join is inside the thumbnail. The whole claim about
          this picture is that it repeats without a seam — drawn once there is nothing to check
          that against, and drawn four times a seam would be a cross through the middle. */}
      <span
        className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-lg border border-slate-300 bg-white"
        style={state === 'ok' ? {
          backgroundImage: `url(${src})`,
          backgroundSize: '50% 50%',
          backgroundRepeat: 'repeat',
        } : undefined}
      >
        {state === 'loading' && spin}
        {state === 'failed' && <span className="text-slate-300">—</span>}
      </span>
      <figcaption className="text-center text-[9.5px] leading-tight text-slate-400">
        {/* WHICH OF THE TWO SURFACES was drawn — the one the call named, a grain or a mottle. It
            is the first thing to check when a material comes out looking like the wrong kind of
            thing. */}
        <span className="block font-sans text-[10px] text-slate-500">
          {st((wrote?.kind ?? pattern) === 'mottle' ? 'notice.3d.texMottle' : 'notice.3d.texGrain')}
        </span>
      </figcaption>
    </figure>
  );
}
